import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, Variants } from 'framer-motion';
import { useCompanyName, useWhatsAppNumber, useFooterSettings as useFooterSettingsContext, useSocialLinks } from '../../contexts/SettingsContext';
import { useFooterSettings } from '../../hooks/useFooterSettings';

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const companyName = useCompanyName();
  const whatsappNumber = useWhatsAppNumber();
  const footerSettings = useFooterSettingsContext();
  const socialLinks = useSocialLinks();
  const { quickLinks: dynamicQuickLinks, legalLinks: dynamicLegalLinks } = useFooterSettings(currentLang);

  const currentYear = new Date().getFullYear();

  // Check if footer is enabled
  if (!footerSettings.footer_enabled) {
    return null;
  }

  const openWhatsApp = () => {
    const phoneNumber = whatsappNumber.replace(/\D/g, '');
    const message = t('footer.whatsappMessage', 'Hello! I would like to know more about your company formation services.');
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const services = [
    { name: t('countries.saudi'), href: '/services/saudi-arabia' },
    { name: t('countries.egypt'), href: '/services/egypt' },
    { name: t('countries.uk'), href: '/services/uk' },
    { name: t('countries.usa'), href: '/services/usa' },
  ];

  // Use dynamic quick links from database, fallback to static ones
  const quickLinks = dynamicQuickLinks.length > 0
    ? dynamicQuickLinks.map(link => ({ name: link.name, href: link.url }))
    : [
        { name: t('nav.home'), href: '/' },
        { name: t('nav.about'), href: '/about' },
        { name: t('nav.contact'), href: '/contact' },
      ];

  // Get company description based on language
  const companyDescription = currentLang === 'ar'
    ? footerSettings.company_description_ar
    : footerSettings.company_description_en;

  // Get section headings based on language
  const servicesHeading = currentLang === 'ar'
    ? footerSettings.services_heading_ar || t('footer.services', 'Our Services')
    : footerSettings.services_heading_en || t('footer.services', 'Our Services');

  const quickLinksHeading = currentLang === 'ar'
    ? footerSettings.quicklinks_heading_ar || t('footer.quickLinks', 'Quick Links')
    : footerSettings.quicklinks_heading_en || t('footer.quickLinks', 'Quick Links');

  const contactHeading = currentLang === 'ar'
    ? footerSettings.contact_heading_ar || t('footer.contactInfo', 'Contact Info')
    : footerSettings.contact_heading_en || t('footer.contactInfo', 'Contact Info');

  const supportHoursText = currentLang === 'ar'
    ? footerSettings.footer_support_hours_ar || t('footer.supportHours', '24/7 Available')
    : footerSettings.footer_support_hours_en || t('footer.supportHours', '24/7 Available');

  const copyrightText = currentLang === 'ar'
    ? footerSettings.copyright_text_ar || t('footer.rights', 'All rights reserved.')
    : footerSettings.copyright_text_en || t('footer.rights', 'All rights reserved.');

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <footer
      className="text-white relative overflow-hidden"
      style={{
        background: `linear-gradient(to bottom right, ${footerSettings.background_gradient_start || '#111827'}, ${footerSettings.background_gradient_end || '#1F2937'})`,
      }}
    >
      {/* Background decorative elements */}
      {footerSettings.animations_enabled && (
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute -top-40 -right-40 w-80 h-80 bg-primary-600 bg-opacity-10 rounded-full"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <motion.div
            className="absolute -bottom-20 -left-20 w-60 h-60 bg-primary-500 bg-opacity-5 rounded-full"
            animate={{
              scale: [1, 0.8, 1],
              rotate: [360, 180, 0],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        </div>
      )}

      <div className="relative">
        {/* Wave separator */}
        {footerSettings.wave_separator_enabled && (
          <div className="relative">
            <svg
              className="w-full h-20 text-white"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 1440 320"
              fill="currentColor"
            >
              <path d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,138.7C960,139,1056,117,1152,117.3C1248,117,1344,139,1392,149.3L1440,160L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
            </svg>
          </div>
        )}

        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            {footerSettings.company_section_enabled && (
              <motion.div className="lg:col-span-1" variants={itemVariants}>
                <div className="flex items-center mb-6">
                  {footerSettings.company_logo_enabled && (
                    <div className="mr-3 rtl:ml-3 rtl:mr-0 flex items-center justify-center">
                      <img
                        src="/images/logo.png"
                        alt={`${companyName} logo`}
                        className="w-10 h-10 object-contain"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <span
                    className="text-2xl font-bold"
                    style={{ color: footerSettings.text_primary_color || '#ffffff' }}
                  >
                    {companyName}
                  </span>
                </div>
                <p
                  className="mb-6 leading-relaxed"
                  style={{ color: footerSettings.text_secondary_color || '#d1d5db' }}
                >
                  {companyDescription}
                </p>

                {/* Social Media Links */}
                {footerSettings.social_links_enabled && (
                  <div className="flex space-x-4 rtl:space-x-reverse">
                    {/* Twitter / X */}
                    {footerSettings.show_twitter && socialLinks.twitter_url && (
                      <motion.a
                        href={socialLinks.twitter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-gray-700 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-all duration-300 group"
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <svg className="w-5 h-5 text-gray-300 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                        </svg>
                      </motion.a>
                    )}

                    {/* LinkedIn */}
                    {footerSettings.show_linkedin && socialLinks.linkedin_url && (
                      <motion.a
                        href={socialLinks.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-gray-700 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-all duration-300 group"
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <svg className="w-5 h-5 text-gray-300 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                      </motion.a>
                    )}

                    {/* Facebook */}
                    {footerSettings.show_facebook && socialLinks.facebook_url && (
                      <motion.a
                        href={socialLinks.facebook_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-gray-700 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-all duration-300 group"
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <svg className="w-5 h-5 text-gray-300 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98a8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                        </svg>
                      </motion.a>
                    )}

                    {/* WhatsApp */}
                    {footerSettings.show_whatsapp_social && (
                      <motion.button
                        onClick={openWhatsApp}
                        className="w-10 h-10 bg-green-600 hover:bg-green-500 rounded-lg flex items-center justify-center transition-all duration-300 group"
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        title={t('contact.whatsapp')}
                      >
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                        </svg>
                      </motion.button>
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {/* Services */}
            {footerSettings.services_section_enabled && (
              <motion.div variants={itemVariants}>
                <h3
                  className="text-xl font-semibold mb-6"
                  style={{ color: footerSettings.text_primary_color || '#ffffff' }}
                >
                  {servicesHeading}
                </h3>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <motion.li
                    key={service.href}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Link
                      to={service.href}
                      className="hover:text-white transition-colors duration-200 flex items-center group"
                      style={{ color: footerSettings.text_secondary_color || '#d1d5db' }}
                    >
                      <svg className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 text-primary-500 group-hover:text-primary-400 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform duration-200">
                        {service.name}
                      </span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
              </motion.div>
            )}

            {/* Quick Links */}
            {footerSettings.quicklinks_section_enabled && (
              <motion.div variants={itemVariants}>
                <h3
                  className="text-xl font-semibold mb-6"
                  style={{ color: footerSettings.text_primary_color || '#ffffff' }}
                >
                  {quickLinksHeading}
                </h3>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <motion.li
                    key={link.href}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Link
                      to={link.href}
                      className="hover:text-white transition-colors duration-200 flex items-center group"
                      style={{ color: footerSettings.text_secondary_color || '#d1d5db' }}
                    >
                      <svg className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 text-primary-500 group-hover:text-primary-400 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform duration-200">
                        {link.name}
                      </span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
              </motion.div>
            )}

            {/* Contact Info */}
            {footerSettings.contact_section_enabled && (
              <motion.div variants={itemVariants}>
                <h3
                  className="text-xl font-semibold mb-6"
                  style={{ color: footerSettings.text_primary_color || '#ffffff' }}
                >
                  {contactHeading}
                </h3>
              <div className="space-y-4">
                {footerSettings.whatsapp_contact_enabled && (
                  <motion.div
                    className="flex items-start group cursor-pointer"
                    onClick={openWhatsApp}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center mr-3 rtl:ml-3 rtl:mr-0 group-hover:bg-primary-700 transition-colors duration-200">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-300 text-sm">{t('footer.whatsapp', 'WhatsApp')}</p>
                      <p className="text-white font-medium group-hover:text-primary-400 transition-colors duration-200">{whatsappNumber}</p>
                    </div>
                  </motion.div>
                )}

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center mr-3 rtl:ml-3 rtl:mr-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm">{t('footer.email', 'Email')}</p>
                    <a
                      href={`mailto:${footerSettings.footer_email || 'info@patrixglobal.com'}`}
                      className="text-white font-medium hover:text-primary-400 transition-colors duration-200"
                    >
                      {footerSettings.footer_email || 'info@patrixglobal.com'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center mr-3 rtl:ml-3 rtl:mr-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm">{t('footer.support', 'Support Hours')}</p>
                    <p className="text-white font-medium">{supportHoursText}</p>
                  </div>
                </div>
              </div>
              </motion.div>
            )}
          </div>

          {/* Bottom Section */}
          {footerSettings.bottom_bar_enabled && (
            <motion.div
              className="border-t border-gray-700 mt-12 pt-8"
              variants={itemVariants}
            >
              <div className="flex flex-col md:flex-row justify-between items-center">
                <motion.p
                  className="text-gray-400 text-sm mb-4 md:mb-0"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  © {currentYear} {companyName}. {copyrightText}
                </motion.p>

                {footerSettings.show_legal_links && dynamicLegalLinks.length > 0 && (
                  <motion.div
                    className="flex items-center space-x-6 rtl:space-x-reverse"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    viewport={{ once: true }}
                  >
                    {dynamicLegalLinks.map(link => (
                      <Link
                        key={link.id}
                        to={link.url}
                        className="text-gray-400 hover:text-white text-sm transition-colors duration-200"
                        target={link.opensNewTab ? '_blank' : undefined}
                        rel={link.opensNewTab ? 'noopener noreferrer' : undefined}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </div>

              <motion.p
                className="text-gray-500 text-xs text-center mt-6"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                viewport={{ once: true }}
              >
                {t('footer.designedBy', 'Designed & Developed by')}{' '}
                <a
                  href="https://cloudx.sa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-gray-300 transition-colors duration-200"
                >
                  {currentLang === 'ar' ? 'كلاودكس' : 'CloudX'}
                </a>
              </motion.p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
